'use client'

import {
  createContext,
  useCallback,
  useContext,
  useRef,
  useState,
  type ReactNode,
  useEffect,
} from 'react'
import api from '@/api'
import { setApiAuthToken } from '@/api'
import { extService } from '@/config/extension.ts'
import { supabase } from '@/services/supabase'

export function getCookieDomain() {
  const hostname = window.location.hostname;
  if (hostname === 'localhost' || hostname === '127.0.0.1' || hostname.match(/^\d+\.\d+\.\d+\.\d+$/)) {
    return '';
  }
  const parts = hostname.split('.');
  if (parts.length > 2) {
    return `.${parts.slice(-2).join('.')}`;
  }
  return `.${hostname}`;
}

export function clearCookie(name: string) {
  const domain = getCookieDomain();
  const domainAttr = domain ? `; domain=${domain}` : '';
  document.cookie = `${name}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT${domainAttr};`;
  document.cookie = `${name}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT;`;
}
// PostHog DISABLED — will re-enable after 1st deployment with new keys
// import posthog from 'posthog-js'
import { toast } from 'sonner'
import { joinOrganization } from '@/api/apis/org/invite'

interface CurrentUser {
  first_name: string | null
  last_name: string | null
  email: string
  is_email_verified: boolean
  updated_at: string
  created_at: string
  user_id: string
  id: string
  default_org: string | null
  avatar: string | null
  profile_completed: boolean
  provider_id: string
  source: string
  phone: string | null
}

interface BootstrapData {
  profile: CurrentUser
  organizations: {
    list: Array<{
      id: string
      name: string
      role: string
      logo: string | null
      joined_at: string
    }>
    default_id: string | null
  }
  invitations: Array<{
    id: string
    email: string
    role: string
    status: string
    org_id: string
    org_name: string
    org_logo: string | null
    created_at: string
  }>
}

interface AuthContextType {
  user: CurrentUser | null
  bootstrapData: BootstrapData | null
  login: (credentials: LoginCredentials) => Promise<LoginResult>
  logout: (
    notify_user?: boolean,
    reload?: boolean
  ) => Promise<{ error?: string }>
  loading: boolean
  isLoggedIn: boolean
  loginWithToken: (
    org_id: string | null,
    turnstile_token?: string
  ) => Promise<{ error?: string }>
  logInWithCustomToken: (token: string) => Promise<{ error?: string } | true>
  customAuthLoading: boolean
  signInWithProvider: () => Promise<
    { success?: boolean; profileCompleted?: boolean } | undefined
  >
  isSubscriptionActive: boolean
  token: string | null
  fetchUserProfile: () => Promise<void>
}

interface LoginCredentials {
  email: string
  password: string
  turnstile_token?: string
}

interface LoginResult {
  success?: boolean
  error?: string
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [currentUser, setCurrentUser] = useState<CurrentUser | null>(null)
  const [bootstrapData, setBootstrapData] = useState<BootstrapData | null>(null)
  const [isLoggedIn, setIsLoggedIn] = useState<boolean>(false)
  const [loading, setLoading] = useState<boolean>(true)
  const [customAuthLoading, setCustomAuthLoading] = useState<boolean>(false)
  const [token, setToken] = useState<string | null>(null)
  const [isSubscriptionActive, setIsSubscriptionActive] =
    useState<boolean>(false)

  // useRef to survive StrictMode double-mount AND prevent concurrent calls
  const bootstrapDone = useRef(false)
  const bootstrapInFlight = useRef(false)

  const loginWithTokenCallback = useCallback(loginWithToken, [])

  useEffect(() => {
    const syncSSO = async () => {
      if (window.location.pathname.includes('/oneauth/')) {
        return
      }
      try {
        const {
          data: { session },
        } = await supabase.auth.getSession()
        if (!session) {
          const cookieVal = document.cookie
            .split('; ')
            .find((row) => row.trim().startsWith('xecubes_sso_session='))
          if (cookieVal) {
            const tokenJson = decodeURIComponent(cookieVal.split('=')[1])
            const ssoData = JSON.parse(tokenJson)
            // Only restore if BOTH tokens are present and non-empty.
            // An empty refresh_token would cause "Refresh Token Not Found" errors.
            if (ssoData.access_token && ssoData.refresh_token) {
              console.log(
                '[SSO] Restoring session in Candidate Portal from shared cookie...'
              )
              const { error } = await supabase.auth.setSession({
                access_token: ssoData.access_token,
                refresh_token: ssoData.refresh_token,
              })
              if (error) {
                // Stale/consumed token — clear the cookie to prevent loops
                console.warn('[SSO] Cookie session restore failed, clearing stale cookie:', error.message)
                clearCookie("xecubes_sso_session")
              }
            } else {
              console.warn('[SSO] Cookie missing access or refresh token — ignoring stale cookie')
              clearCookie("xecubes_sso_session")
            }
          }
        }
      } catch (e) {
        console.error('[SSO] Error during initial cookie sync:', e)
      }
    }
    syncSSO()
  }, [])

  useEffect(() => {
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (_event, session) => {
      if (!session) {
        // Clear cookie
        clearCookie("xecubes_sso_session")
        clearCookie("xecubes_active_portal")
        // No session — clear everything
        setApiAuthToken(null)
        setBootstrapData(null)
        setCurrentUser(null)
        setIsLoggedIn(false)
        setToken(null)
        setLoading(false)
        bootstrapDone.current = false
        return
      }

      // Clear any lingering logout markers
      clearCookie('xecubes_logout_event');

      // Set cookie
      const ssoData = {
        access_token: session.access_token,
        refresh_token: session.refresh_token,
      }
      const userSession = {
        id: session.user.id,
        email: session.user.email,
        name: session.user.user_metadata?.first_name 
          ? `${session.user.user_metadata.first_name} ${session.user.user_metadata.last_name || ''}`.trim()
          : (session.user.user_metadata?.full_name || session.user.email?.split('@')[0] || ''),
        first_name: session.user.user_metadata?.first_name || '',
        last_name: session.user.user_metadata?.last_name || '',
        portal: 'candidate',
        portalUrl: window.location.origin,
        updatedAt: Date.now(),
      };
      document.cookie = `xecubes_sso_session=${encodeURIComponent(JSON.stringify(ssoData))}; path=/; max-age=604800; SameSite=Lax${domainAttr}`;
      document.cookie = `xecubes_user_session=${encodeURIComponent(JSON.stringify(userSession))}; path=/; max-age=604800; SameSite=Lax${domainAttr}`;
      document.cookie = `xecubes_sso_active=true; path=/; max-age=604800; SameSite=Lax${domainAttr}`;
      document.cookie = `xecubes_active_portal=${encodeURIComponent(window.location.origin)}; path=/; max-age=604800; SameSite=Lax${domainAttr}`;

      try {
        const channel = new BroadcastChannel('xecubes_auth_channel');
        channel.postMessage({ type: 'LOGIN', user: userSession, timestamp: Date.now() });
        channel.close();
      } catch (_) {}

      // WORLD-CLASS: Infinity Gate Management
      // If we are on a oneauth handover path, we explicitly release any old logout locks
      // as the user has demonstrated a fresh intent to authenticate.
      if (window.location.pathname.includes('/oneauth/')) {
        localStorage.removeItem('auth_gate_lock')
      }

      // WORLD-CLASS: Infinity Gate Guard
      if (localStorage.getItem('auth_gate_lock') === 'active') {
        console.warn(
          '[AUTH] Infinity Gate: Blocking ghost session re-hydration.'
        )
        setLoading(false)
        return
      }

      // Always keep the API header in sync
      setApiAuthToken(session.access_token)

      // Only bootstrap if NO handover is in progress
      if (!bootstrapDone.current && !bootstrapInFlight.current) {
        bootstrapDone.current = true
        bootstrapInFlight.current = true
        setToken(session.access_token)
        setLoading(true)
        await loginWithTokenCallback(null)
        bootstrapInFlight.current = false
      }
    })

    // Safety fallback: if no session exists at all, stop loading
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (!session) setLoading(false)
    })

    return () => {
      subscription.unsubscribe()
    }
  }, [loginWithTokenCallback])

  // WORLD-CLASS: Dynamic Handover Hydration
  // If we skipped bootstrap because we were on a /oneauth/ page,
  // we must trigger it as soon as we navigate away to a destination.
  useEffect(() => {
    const triggerBootstrapIfNecessary = async () => {
      const {
        data: { session },
      } = await supabase.auth.getSession()

      if (session && !bootstrapDone.current && !bootstrapInFlight.current) {
        if (!window.location.pathname.includes('/oneauth/')) {
          console.log(
            '[Auth] Detected session after handover. Triggering bootstrap...'
          )
          bootstrapDone.current = true
          bootstrapInFlight.current = true
          setToken(session.access_token)
          setLoading(true)
          await loginWithTokenCallback(null)
          bootstrapInFlight.current = false
        }
      }
    }

    triggerBootstrapIfNecessary()
  }, [window.location.pathname, loginWithTokenCallback])

  useEffect(() => {
    // AUTO-JOIN HOOK: If we are logged in and have a pending invite in sessionStorage, accept it automatically.
    const autoJoin = async () => {
      const pendingInviteId = sessionStorage.getItem('pending_invite_id')
      if (isLoggedIn && pendingInviteId && !loading) {
        try {
          console.debug(`[AUTH] Auto-joining invitation: ${pendingInviteId}`)
          await joinOrganization(pendingInviteId)

          // Clear intent after success
          sessionStorage.removeItem('pending_invite_id')
          toast.success(`Welcome aboard! You have joined the organization.`)

          // Refresh session to pick up the new metadata claims from backend
          await supabase.auth.refreshSession()

          // Redirect to dashboard
          window.location.replace('/')
        } catch (err: any) {
          console.error(
            '[AUTH] Auto-join failed:',
            err.response?.data?.message || err.message
          )
          // If it fails (e.g. invite already used), we clear it so it doesn't keep trying
          sessionStorage.removeItem('pending_invite_id')
        }
      }
    }

    autoJoin()
  }, [isLoggedIn, loading])

  function getAuthErrorMessage(errorCode?: string): string {
    return `Authentication failed: ${errorCode || 'Invalid credentials'}`
  }

  const logout = useCallback(
    async (notify_user = true, reload = true): Promise<{ error?: string }> => {
      setLoading(true)
      try {
        // 1. ENGAGE INFINITY GATE: Deterministic Lockout
        localStorage.setItem('auth_gate_lock', 'active')

        // 2. BACKEND HANDSHAKE: Notify backend
        try {
          await api.post('/candidate/auth/sign-out').catch(() => {})
        } catch (_err) {
          console.warn('[AUTH] Backend sign-out notify failed (ignoring)')
        }

        // 3. ENTERPRISE KILL SWITCH: Revoke all platforms
        await supabase.auth.signOut({ scope: 'global' })

        // 4. NUCLEAR PURGE: Explicitly wipe all auth-related markers
        localStorage.removeItem('auth_backend_synced')
        Object.keys(localStorage).forEach((key) => {
          if (key.startsWith('sb-') && key.endsWith('-auth-token')) {
            localStorage.removeItem(key)
          }
        })

        // 5. COOKIE BURNER: Industrial Cleaning of Supabase Session Cookies
        clearCookie("xecubes_sso_session")
        clearCookie("xecubes_user_session")
        clearCookie("xecubes_sso_active")
        clearCookie("xecubes_active_portal")
        document.cookie.split(';').forEach((c) => {
          const cookie = c.trim()
          if (cookie.startsWith('sb-') && cookie.includes('-auth-token')) {
            const name = cookie.split('=')[0]
            document.cookie = `${name}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; SameSite=Lax;`
            const domain = getCookieDomain();
            const domainAttr = domain ? `; domain=${domain}` : '';
            document.cookie = `${name}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT${domainAttr}; SameSite=Lax;`
          }
        })

        try {
          const channel = new BroadcastChannel('xecubes_auth_channel');
          channel.postMessage({ type: 'LOGOUT', timestamp: Date.now() });
          channel.close();
        } catch (_) {}

        // Clear all state
        setBootstrapData(null)
        setCurrentUser(null)
        setToken(null)
        setApiAuthToken(null)
        setIsLoggedIn(false)

        // WORLD-CLASS TELEMETRY: Reset PostHog on Logout
        // posthog.reset()

        // Clear IndexedDB data
        window.indexedDB.deleteDatabase('recruiter-tags-db')

        if (notify_user) toast.success('Logged out')

        await extService({
          type: 'LOGOUT',
        }).catch(() => {})

        return {}
      } catch (error: any) {
        console.error('Error during sign out', error)
        return { error: error.message }
      } finally {
        if (reload) {
          window.location.replace('/login') // Use replace to clear history stack
        }
        setLoading(false)
      }
    },
    []
  )

  const login = async ({
    email,
    password,
    turnstile_token,
  }: LoginCredentials): Promise<LoginResult> => {
    setLoading(true)
    try {
      // RELEASE INFINITY GATE: Allow fresh session
      localStorage.removeItem('auth_gate_lock')

      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
      })

      if (error) throw error

      const { error: tokenError = null } = await loginWithToken(
        null,
        turnstile_token
      )

      if (tokenError) {
        toast.error(tokenError)
        setLoading(false)
        return { error: tokenError, success: false }
      }

      return { success: true }
    } catch (error: any) {
      toast.error(getAuthErrorMessage(error.message))
      setLoading(false)
      return {
        error: error.message,
      }
    }
  }

  async function loginWithToken(
    org_id: string | null,
    _turnstile_token?: string
  ): Promise<{ error?: string }> {
    try {
      bootstrapDone.current = true
      const response = await api.post(`/candidate/auth/token`, {
        org_id: org_id || null,
      })
      // Candidates don't have subscriptions — gracefully handle missing field
      setIsSubscriptionActive(
        response.data.data.is_subscription_active || false
      )

      // In Supabase, tokens are usually handled automatically by the client,
      // but we store it here for compatibility with existing API calls
      if (response.data.data.token) {
        setToken(response.data.data.token)
        setApiAuthToken(response.data.data.token)
      }
      await fetchBootstrapData()
      await extService({
        token: response.data.data.token,
        type: 'AUTH_TOKEN',
      }).catch(() => {
        /* Extension not available — expected in non-Chrome environments */
      })

      return {}
    } catch (e: any) {
      const error = e?.response?.data?.message
      const status = e?.response?.status

      if (status === 429) {
        toast.error('Too many requests. Please wait a moment.')
        setLoading(false)
        return { error: 'Rate limit exceeded' }
      }

      // DIRECT IDENTITY RESILIENCE: If the token endpoint returns 404,
      // attempt direct bootstrap. This handles initial setup and deployment gaps.
      if (status === 404) {
        console.warn(
          '[AUTH:DirectIdentity] Token endpoint not found. Attempting direct bootstrap...'
        )
        try {
          await fetchBootstrapData()
          return {}
        } catch (bootstrapErr) {
          console.error(
            '[AUTH] Direct bootstrap fallback also failed:',
            bootstrapErr
          )
        }
      }

      setLoading(false)
      await logout(false, false)
      if (String(error).toLowerCase().includes('not found')) {
        return {
          error: `user is not registered! Please Signup.`,
        }
      } else {
        return {
          error:
            'Oops! Something went wrong on our end. Please try again later.',
        }
      }
    }
  }

  const fetchBootstrapData = async (): Promise<void> => {
    try {
      setLoading(true)
      const res = await api.get(`/candidate/bootstrap`)
      const data = res.data.data as BootstrapData

      // DIRECT IDENTITY: Candidate bootstrap doesn't include org data by default.
      // Provide safe defaults so OrgProvider & SubscriptionProvider don't crash.
      if (!data.organizations) {
        data.organizations = { list: [], default_id: null }
      }
      if (!data.invitations) {
        data.invitations = []
      }

      setBootstrapData(data)
      setCurrentUser(data.profile)
      setIsLoggedIn(true)

      // WORLD-CLASS TELEMETRY: Identify User and Organization
      if (data.profile) {
        // posthog.identify(data.profile.user_id, {
        //   email: data.profile.email,
        //   first_name: data.profile.first_name,
        //   last_name: data.profile.last_name,
        // })
      }

      if (data.organizations?.default_id) {
        // posthog.group('organization', data.organizations.default_id, {
        //   name:
        //     data.organizations.list.find(
        //       (o) => o.id === data.organizations.default_id
        //     )?.name || 'Unknown',
        // })
      }

      // INDUSTRY-STANDARD: Autonomous Portal Pivot (APP)
      // If the bootstrap detects the user's identity belongs to another portal,
      // we autonomously pivot them to the correct destination.
      // EXCEPTION: Do not pivot if the user is visiting a public portfolio page.
      const isPublicPortfolioPage =
        window.location.pathname.startsWith('/portfolio/')
      const portalRole = (data.profile as any)?.portal_role
      if (
        portalRole &&
        portalRole !== 'CANDIDATE' &&
        !isPublicPortfolioPage &&
        (data as any).portal_urls
      ) {
        const pivotMap: Record<string, string> = {
          RECRUITER: (data as any).portal_urls.recruiter,
          COLLEGE_ADMIN: (data as any).portal_urls.college,
          COLLEGE_OFFICER: (data as any).portal_urls.college,
        }

        const targetUrl = pivotMap[portalRole]
        if (targetUrl && !window.location.origin.includes(targetUrl)) {
          console.info(
            `[Auth:APP] Identity Pivot Initiated: ${portalRole} -> ${targetUrl}`
          )
          window.location.replace(`${targetUrl}/auth/callback?token=${token}`)
          return
        }
      }
    } catch (err) {
      console.error('Bootstrap failed', err)
      setIsLoggedIn(false)
      setCurrentUser(null)
      setBootstrapData(null)
    } finally {
      setLoading(false)
    }
  }

  const logInWithCustomToken = async (
    token: string
  ): Promise<{ error?: string } | true> => {
    try {
      // The custom token is a magic link hashed_token from the backend's
      // generateLink({ type: 'magiclink' }). verifyOtp is the correct API
      // to consume it — it produces a full session with a real refresh token.
      const { error } = await supabase.auth.verifyOtp({
        token_hash: token,
        type: 'magiclink',
      })
      if (error) return { error: error.message }
    } catch (error: any) {
      return { error: error.message }
    }

    return true
  }

  const signInWithProvider = async (): Promise<
    { success?: boolean; profileCompleted?: boolean } | undefined
  > => {
    setCustomAuthLoading(true)

    try {
      // RELEASE INFINITY GATE: Allow fresh session
      localStorage.removeItem('auth_gate_lock')

      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: window.location.origin + '/callback/google-oauth',
        },
      })

      if (error) throw error

      // Note: OAuth flows usually redirect, so further logic happens in the callback page
      return { success: true }
    } catch (error: any) {
      toast.error('Authentication failed: ' + error.message)
    } finally {
      setCustomAuthLoading(false)
    }
  }

  const value: AuthContextType = {
    user: currentUser,
    bootstrapData,
    login,
    token,
    logout,
    loading,
    isLoggedIn,
    loginWithToken,
    logInWithCustomToken,
    customAuthLoading,
    signInWithProvider,
    isSubscriptionActive,
    fetchUserProfile: fetchBootstrapData,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
